import type { ErrorReason, GroupStatus } from '@/domain/groups/types'
import { Icon, type IconGlyph } from '@sunwebgroup/uds-ui/icon'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import cn from 'classnames'
import { useIntl } from '@/intl'
import styles from './StatusNotification.module.css'

type GroupStatusType = GroupStatus['status']
type IconTone = 'green' | 'red'
type StatusConfigKey = GroupStatusType | `${GroupStatusType}:${ErrorReason}`

const statusConfig: Partial<Record<StatusConfigKey, {
  icon: IconGlyph
  title: string
  text: string
  tone?: IconTone
}>> = {
  'expired': {
    icon: 'FaceSad',
    title: 'groups_status_notification_expired_title',
    text: 'groups_status_notification_expired_description',
  },
  'success': {
    icon: 'CheckMarkInCircle',
    title: 'groups_status_notification_success_title',
    text: 'groups_status_notification_success_description',
    tone: 'green',
  },
  'full': {
    icon: 'FaceSad',
    title: 'groups_status_notification_full_title',
    text: 'groups_status_notification_full_description',
  },
  'error': {
    icon: 'ExclamationInTriangle',
    title: 'groups_status_notification_error_title',
    text: 'groups_status_notification_error_description',
    tone: 'red',
  },
  'blocked': {
    icon: 'InformationInCircle',
    title: 'groups_status_notification_blocked_title',
    text: 'groups_status_notification_blocked_description',
  },
  'error:ParticipantAlreadyExists': {
    icon: 'ExclamationInTriangle',
    title: 'groups_status_notification_participant_exists_title',
    text: 'groups_status_notification_participant_exists_description',
    tone: 'red',
  },
  'error:BookingFull': {
    icon: 'ExclamationInTriangle',
    title: 'groups_status_notification_booking_full_title',
    text: 'groups_status_notification_booking_full_description',
    tone: 'red',
  },
}

interface StatusMessageProps {
  status: GroupStatusType | undefined
  groupName: string | undefined
  errorReason?: ErrorReason
}

export function StatusNotification({ status, groupName, errorReason }: StatusMessageProps) {
  const intl = useIntl()

  const config
    = (status && errorReason && statusConfig[`${status}:${errorReason}` as StatusConfigKey])
      || statusConfig[status ?? 'error']
      || statusConfig.error!

  return (
    <div className={styles.wrapper}>
      <div className={styles.card}>
        <div className={styles.content}>
          <div
            className={cn(
              styles.iconWrapper,
              config.tone === 'green' && styles.colorGreen,
              config.tone === 'red' && styles.colorRed,
            )}
          >
            <Icon glyph={config.icon} size={40} />
          </div>
          <Typography variant="body-m-bold-snug">
            {intl.formatMessage({ id: config.title }, { groupName })}
          </Typography>
          <Typography variant="body-s-regular-snug">
            {intl.formatMessage({ id: config.text }, { groupName })}
          </Typography>
        </div>
      </div>
    </div>
  )
}
